/**
 * Address constants + validation shared by the authenticated PropertyForm and
 * the anonymous intake funnel. Keeping the US-state list and the ZIP/state rules
 * in one place means the two entry points can't validate addresses differently.
 */

export interface UsState {
  code: string;
  name: string;
}

export const usStates: ReadonlyArray<UsState> = [
  { code: "AL", name: "Alabama" },
  { code: "AK", name: "Alaska" },
  { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" },
  { code: "CA", name: "California" },
  { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" },
  { code: "DE", name: "Delaware" },
  { code: "DC", name: "District of Columbia" },
  { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" },
  { code: "HI", name: "Hawaii" },
  { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" },
  { code: "IN", name: "Indiana" },
  { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" },
  { code: "KY", name: "Kentucky" },
  { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" },
  { code: "MD", name: "Maryland" },
  { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" },
  { code: "MN", name: "Minnesota" },
  { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" },
  { code: "MT", name: "Montana" },
  { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" },
  { code: "NH", name: "New Hampshire" },
  { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" },
  { code: "NY", name: "New York" },
  { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" },
  { code: "OH", name: "Ohio" },
  { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" },
  { code: "PA", name: "Pennsylvania" },
  { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" },
  { code: "SD", name: "South Dakota" },
  { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" },
  { code: "UT", name: "Utah" },
  { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" },
  { code: "WA", name: "Washington" },
  { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" },
  { code: "WY", name: "Wyoming" },
];

export function isValidStateCode(code: string): boolean {
  const normalized = code.trim().toUpperCase();
  return usStates.some((s) => s.code === normalized);
}

export interface AddressInput {
  street: string;
  unit: string;
  city: string;
  state: string;
  postalCode: string;
}

export interface NormalizedAddress {
  street: string;
  unit?: string;
  city: string;
  state: string;
  postalCode: string;
}

/**
 * Validate the street/city/state/ZIP fields the funnel collects. Mirrors the
 * rules in PropertyForm (and the API): required street/city, valid US state,
 * clean 5-digit ZIP (ZIP+4 accepted — first five kept). Returns per-field
 * errors and, when clean, the normalized values ready for `POST /properties`.
 */
export function validateAddress(input: AddressInput): {
  errors: Partial<Record<keyof AddressInput, string>>;
  normalized: NormalizedAddress | null;
} {
  const street = input.street.trim();
  const city = input.city.trim();
  const state = input.state.trim().toUpperCase();
  const zipDigits = input.postalCode.replace(/\D/g, "");

  const errors: Partial<Record<keyof AddressInput, string>> = {};
  if (!street) errors.street = "Street is required";
  if (!city) errors.city = "City is required";
  if (!state) errors.state = "State is required";
  else if (!isValidStateCode(state)) errors.state = "Select a valid US state";
  if (!input.postalCode.trim()) errors.postalCode = "ZIP is required";
  else if (zipDigits.length < 5) errors.postalCode = "Enter a valid 5-digit ZIP code";

  if (Object.keys(errors).length > 0) return { errors, normalized: null };

  return {
    errors,
    normalized: {
      street,
      unit: input.unit.trim() ? input.unit.trim() : undefined,
      city,
      state,
      postalCode: zipDigits.slice(0, 5),
    },
  };
}

/**
 * Derive a sensible default property nickname from the street line so the funnel
 * doesn't have to ask for one (the API requires a nickname). Falls back to a
 * generic label for empty/over-long input. Users can rename later in the app.
 */
export function deriveNickname(street: string): string {
  const trimmed = street.trim();
  if (!trimmed) return "My property";
  return trimmed.length > 60 ? trimmed.slice(0, 60) : trimmed;
}
